import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "row", "noResults", "columnToggle", "table"]
  static values = {
    storageKey: { type: String, default: "search-results-columns" },
    filterStorageKey: { type: String, default: "" }, // Set to enable filter persistence
    sortStorageKey: { type: String, default: "" }, // Set to enable sort persistence
    clearOnLoad: { type: Boolean, default: false } // Clear filters when page loads (for CSV imports)
  }

  // Store all filter values
  filters = {}
  // Store hidden columns
  hiddenColumns = new Set()
  // Store current sort state
  sortColumn = null
  sortDirection = 'asc' // 'asc' or 'desc'

  connect() {
    // Initialize filters object for each input
    this.inputTargets.forEach(input => {
      const column = input.dataset.column
      this.filters[column] = ""
    })

    // Load hidden columns from localStorage
    this.loadColumnPreferences()
    this.applyColumnVisibility()

    // Load saved filters from localStorage
    this.loadFilterPreferences()

    // Initialize sortable headers and load saved sort
    this.initSortableHeaders()
    this.loadSortPreferences()

    // Observe tbody for new rows (for Turbo Streams auto-show)
    this.observeTbodyForChanges()
  }

  observeTbodyForChanges() {
    const tbody = this.element.querySelector('tbody')
    if (!tbody) return

    // Show the table wrapper if tbody has content
    const showIfHasContent = () => {
      if (tbody.children.length > 0) {
        this.element.classList.remove('hidden')
      }
    }

    // Check initially
    showIfHasContent()

    // Observe for changes (Turbo Streams appends)
    this.tbodyObserver = new MutationObserver(showIfHasContent)
    this.tbodyObserver.observe(tbody, { childList: true })
  }

  disconnect() {
    if (this.tbodyObserver) {
      this.tbodyObserver.disconnect()
    }
  }

  filter(event) {
    const input = event.target
    const column = input.dataset.column
    const value = input.value.toLowerCase().trim()

    // Update filter value
    this.filters[column] = value

    // Save filters to localStorage
    this.saveFilterPreferences()

    // Apply all filters
    this.applyFilters()
  }

  applyFilters() {
    let visibleCount = 0

    this.rowTargets.forEach(row => {
      let visible = true

      // Check each filter
      for (const [column, filterValue] of Object.entries(this.filters)) {
        if (filterValue === "") continue

        // Special handling for status (matches row data-status attribute)
        if (column === "status") {
          const rowStatus = row.dataset.status || ""
          if (rowStatus !== filterValue) {
            visible = false
            break
          }
          continue
        }

        // Special handling for results (numeric minimum - from row data-results attribute)
        if (column === "results") {
          const minResults = parseInt(filterValue, 10)
          const rowResults = parseInt(row.dataset.results || "0", 10)
          if (isNaN(minResults) || rowResults < minResults) {
            visible = false
            break
          }
          continue
        }

        const cell = row.querySelector(`[data-column="${column}"]`)
        if (!cell) continue

        // Special handling for rating (numeric comparison - minimum value)
        if (column === "rating") {
          const minRating = parseFloat(filterValue)
          const cellRating = parseFloat(cell.dataset.rating || "0")
          if (isNaN(minRating) || cellRating < minRating) {
            visible = false
            break
          }
        }
        // Special handling for AI score (numeric comparison - minimum value)
        else if (column === "ai-score") {
          const minScore = parseFloat(filterValue)
          const cellScore = parseFloat(cell.dataset.aiScore || "0")
          if (isNaN(minScore) || cellScore < minScore) {
            visible = false
            break
          }
        }
        else {
          // Text-based filtering
          const cellText = cell.textContent.toLowerCase().trim()
          if (!cellText.includes(filterValue)) {
            visible = false
            break
          }
        }
      }

      row.style.display = visible ? "" : "none"
      if (visible) visibleCount++
    })

    // Show/hide no results message
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.style.display = visibleCount === 0 ? "" : "none"
    }

    return visibleCount
  }

  clearAll() {
    this.inputTargets.forEach(input => {
      input.value = ""
      const column = input.dataset.column
      this.filters[column] = ""
    })
    this.saveFilterPreferences()
    this.applyFilters()
  }

  // Column visibility methods
  toggleColumn(event) {
    const columnName = event.target.dataset.columnName
    const isChecked = event.target.checked

    if (isChecked) {
      this.hiddenColumns.delete(columnName)
    } else {
      this.hiddenColumns.add(columnName)
    }

    this.saveColumnPreferences()
    this.applyColumnVisibility()
  }

  applyColumnVisibility() {
    // Get all column headers and cells
    const table = this.hasTableTarget ? this.tableTarget : this.element.querySelector("table")
    if (!table) return

    const headers = table.querySelectorAll("th[data-column-name]")
    const rows = table.querySelectorAll("tbody tr")

    // Build column index map
    const columnIndexes = {}
    headers.forEach((th, index) => {
      const name = th.dataset.columnName
      if (name) {
        columnIndexes[name] = index
      }
    })

    // Apply visibility to headers
    headers.forEach(th => {
      const name = th.dataset.columnName
      th.style.display = this.hiddenColumns.has(name) ? "none" : ""
    })

    // Apply visibility to cells
    rows.forEach(row => {
      const cells = row.querySelectorAll("td[data-column-name]")
      cells.forEach(td => {
        const name = td.dataset.columnName
        td.style.display = this.hiddenColumns.has(name) ? "none" : ""
      })
    })

    // Update checkboxes
    this.columnToggleTargets.forEach(checkbox => {
      const name = checkbox.dataset.columnName
      checkbox.checked = !this.hiddenColumns.has(name)
    })
  }

  loadColumnPreferences() {
    try {
      const saved = localStorage.getItem(this.storageKeyValue)
      if (saved) {
        const hidden = JSON.parse(saved)
        this.hiddenColumns = new Set(hidden)
      }
    } catch (e) {
      console.warn("Could not load column preferences", e)
    }
  }

  saveColumnPreferences() {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify([...this.hiddenColumns]))
    } catch (e) {
      console.warn("Could not save column preferences", e)
    }
  }

  resetColumns() {
    this.hiddenColumns.clear()
    this.saveColumnPreferences()
    this.applyColumnVisibility()
  }

  // Filter persistence methods
  loadFilterPreferences() {
    if (!this.filterStorageKeyValue) return

    try {
      const saved = localStorage.getItem(this.filterStorageKeyValue)
      if (saved) {
        const savedFilters = JSON.parse(saved)

        // Restore filter values to inputs and internal state
        this.inputTargets.forEach(input => {
          const column = input.dataset.column
          if (savedFilters[column]) {
            input.value = savedFilters[column]
            this.filters[column] = savedFilters[column].toLowerCase().trim()
          }
        })

        // Apply the restored filters
        this.applyFilters()
      }
    } catch (e) {
      console.warn("Could not load filter preferences", e)
    }
  }

  saveFilterPreferences() {
    if (!this.filterStorageKeyValue) return

    try {
      // Save original (non-lowercased) values from inputs
      const filtersToSave = {}
      this.inputTargets.forEach(input => {
        const column = input.dataset.column
        if (input.value) {
          filtersToSave[column] = input.value
        }
      })
      localStorage.setItem(this.filterStorageKeyValue, JSON.stringify(filtersToSave))
    } catch (e) {
      console.warn("Could not save filter preferences", e)
    }
  }

  // Sorting methods
  initSortableHeaders() {
    const table = this.hasTableTarget ? this.tableTarget : this.element.querySelector("table")
    if (!table) return

    const headers = table.querySelectorAll("th[data-column-name][data-sortable]")
    headers.forEach(th => {
      th.classList.add('cursor-pointer', 'select-none', 'hover:bg-slate-100', 'dark:hover:bg-slate-700')
      th.addEventListener('click', () => this.sort(th.dataset.columnName))

      // Add sort indicator span if not present
      if (!th.querySelector('.sort-indicator')) {
        const indicator = document.createElement('span')
        indicator.className = 'sort-indicator ml-1 text-slate-400'
        indicator.innerHTML = '↕'
        th.appendChild(indicator)
      }
    })
  }

  sort(columnName) {
    // Toggle direction if same column, otherwise reset to asc
    if (this.sortColumn === columnName) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortColumn = columnName
      this.sortDirection = 'asc'
    }

    this.applySort()
    this.saveSortPreferences()
    this.updateSortIndicators()
  }

  applySort() {
    if (!this.sortColumn) return

    const table = this.hasTableTarget ? this.tableTarget : this.element.querySelector("table")
    if (!table) return

    const tbody = table.querySelector('tbody')
    if (!tbody) return

    const rows = Array.from(tbody.querySelectorAll('tr'))

    rows.sort((a, b) => {
      const cellA = a.querySelector(`td[data-column-name="${this.sortColumn}"]`)
      const cellB = b.querySelector(`td[data-column-name="${this.sortColumn}"]`)

      if (!cellA || !cellB) return 0

      // Get sort value from data attribute or text content
      let valueA = cellA.dataset.sortValue ?? cellA.textContent.trim().toLowerCase()
      let valueB = cellB.dataset.sortValue ?? cellB.textContent.trim().toLowerCase()

      // Try to parse as numbers
      const numA = parseFloat(valueA)
      const numB = parseFloat(valueB)

      let comparison = 0
      if (!isNaN(numA) && !isNaN(numB)) {
        comparison = numA - numB
      } else {
        comparison = valueA.localeCompare(valueB)
      }

      return this.sortDirection === 'asc' ? comparison : -comparison
    })

    // Re-append rows in sorted order
    rows.forEach(row => tbody.appendChild(row))
  }

  updateSortIndicators() {
    const table = this.hasTableTarget ? this.tableTarget : this.element.querySelector("table")
    if (!table) return

    const headers = table.querySelectorAll("th[data-column-name][data-sortable]")
    headers.forEach(th => {
      const indicator = th.querySelector('.sort-indicator')
      if (!indicator) return

      if (th.dataset.columnName === this.sortColumn) {
        indicator.innerHTML = this.sortDirection === 'asc' ? '↑' : '↓'
        indicator.classList.remove('text-slate-400')
        indicator.classList.add('text-primary-600')
      } else {
        indicator.innerHTML = '↕'
        indicator.classList.remove('text-primary-600')
        indicator.classList.add('text-slate-400')
      }
    })
  }

  loadSortPreferences() {
    if (!this.sortStorageKeyValue) return

    try {
      const saved = localStorage.getItem(this.sortStorageKeyValue)
      if (saved) {
        const { column, direction } = JSON.parse(saved)
        if (column) {
          this.sortColumn = column
          this.sortDirection = direction || 'asc'
          this.applySort()
          this.updateSortIndicators()
        }
      }
    } catch (e) {
      console.warn("Could not load sort preferences", e)
    }
  }

  saveSortPreferences() {
    if (!this.sortStorageKeyValue) return

    try {
      localStorage.setItem(this.sortStorageKeyValue, JSON.stringify({
        column: this.sortColumn,
        direction: this.sortDirection
      }))
    } catch (e) {
      console.warn("Could not save sort preferences", e)
    }
  }
}
